export interface TrustSection {
  heading: string;
  paragraphs: string[];
}

export interface TrustPage {
  title: string;
  metaDescription: string;
  intro: string[];
  sections: TrustSection[];
}

export const aboutPage: Record<string, TrustPage> = {
  en: {
    title: 'About USFANSLINK',
    metaDescription: 'Learn about USFANSLINK — an independent informational resource about the USFANS spreadsheet, built to help you research products, sellers and buying resources.',
    intro: [
      'USFANSLINK is an independent informational resource. We help people research the USFANS spreadsheet, product categories, sellers, shipping and buying guides without wading through scattered or confusing information.',
      'We are not affiliated with, endorsed by, or sponsored by any seller, platform or brand mentioned on this site. Our goal is simple: give you clear, original, and honest guidance so you can make your own informed decisions.',
    ],
    sections: [
      {
        heading: 'What We Do',
        paragraphs: [
          'We publish original guides, reviews and buying resources focused on the USFANS spreadsheet. Every page is written to be useful first — not to push you toward a specific purchase.',
          'We organize product categories, explain how to check sellers and shipping, and point you toward the main website so you can continue your research in one place.',
        ],
      },
      {
        heading: 'What We Do Not Do',
        paragraphs: [
          'We do not guarantee sellers, products, prices, shipping outcomes, refunds or delivery times. We do not publish made-up coupons, discounts, ratings or statistics.',
          'Whenever current information cannot be verified, we say so clearly and encourage you to check the current official information before making a purchase.',
        ],
      },
      {
        heading: 'Our Editorial Approach',
        paragraphs: [
          'Content is created with a focus on clarity, accuracy and honesty. We review and update pages when meaningful changes occur, and we link between related guides to help you find what you need.',
          'Read our editorial policy to learn more about how we create and maintain content.',
        ],
      },
    ],
  },
  es: {
    title: 'Acerca de USFANSLINK',
    metaDescription: 'Conoce USFANSLINK, un recurso informativo independiente sobre el spreadsheet de USFANS, creado para ayudarte a investigar productos, vendedores y recursos de compra.',
    intro: [
      'USFANSLINK es un recurso informativo independiente. Ayudamos a investigar el spreadsheet de USFANS, las categorías de productos, los vendedores, el envío y las guías de compra sin navegar entre información dispersa o confusa.',
      'No estamos afiliados, respaldados ni patrocinados por ningún vendedor, plataforma o marca mencionada en este sitio. Nuestro objetivo es sencillo: darte orientación clara, original y honesta para que tomes tus propias decisiones informadas.',
    ],
    sections: [
      {
        heading: 'Qué hacemos',
        paragraphs: [
          'Publicamos guías, reseñas y recursos de compra originales centrados en el spreadsheet de USFANS. Cada página está pensada para ser útil primero, no para empujarte hacia una compra concreta.',
          'Organizamos categorías de productos, explicamos cómo revisar vendedores y envío, y te dirigimos al sitio principal para que sigas investigando en un solo lugar.',
        ],
      },
      {
        heading: 'Qué no hacemos',
        paragraphs: [
          'No garantizamos vendedores, productos, precios, resultados de envío, reembolsos ni plazos de entrega. No publicamos cupones, descuentos, valoraciones ni estadísticas inventadas.',
          'Cuando la información actual no se puede verificar, lo decimos claramente y te animamos a revisar la información oficial actual antes de comprar.',
        ],
      },
      {
        heading: 'Nuestro enfoque editorial',
        paragraphs: [
          'El contenido se crea con un enfoque en la claridad, la precisión y la honestidad. Revisamos y actualizamos las páginas cuando hay cambios significativos, y enlazamos guías relacionadas para ayudarte a encontrar lo que necesitas.',
          'Lee nuestra política editorial para saber más sobre cómo creamos y mantenemos el contenido.',
        ],
      },
    ],
  },
  fr: {
    title: 'À propos d\u2019USFANSLINK',
    metaDescription: 'Découvrez USFANSLINK, une ressource informative indépendante sur le spreadsheet USFANS, créée pour vous aider à rechercher produits, vendeurs et ressources d\u2019achat.',
    intro: [
      'USFANSLINK est une ressource informative indépendante. Nous aidons à rechercher le spreadsheet USFANS, les catégories de produits, les vendeurs, la livraison et les guides d\u2019achat sans naviguer dans des informations dispersées ou confuses.',
      'Nous ne sommes affiliés, soutenus ni sponsorisés par aucun vendeur, aucune plateforme ou marque mentionnés sur ce site. Notre objectif est simple : vous donner des conseils clairs, originaux et honnêtes pour prendre vos propres décisions éclairées.',
    ],
    sections: [
      {
        heading: 'Ce que nous faisons',
        paragraphs: [
          'Nous publions des guides, avis et ressources d\u2019achat originaux centrés sur le spreadsheet USFANS. Chaque page est pensée pour être utile d\u2019abord, pas pour vous pousser vers un achat précis.',
          'Nous organisons les catégories de produits, expliquons comment vérifier les vendeurs et la livraison, et vous orientons vers le site principal pour poursuivre vos recherches au même endroit.',
        ],
      },
      {
        heading: 'Ce que nous ne faisons pas',
        paragraphs: [
          'Nous ne garantissons ni vendeurs, ni produits, ni prix, ni résultats de livraison, ni remboursements, ni délais. Nous ne publions pas de coupons, remises, notes ou statistiques inventés.',
          'Quand l\u2019information actuelle ne peut pas être vérifiée, nous le disons clairement et vous encourageons à consulter l\u2019information officielle actuelle avant d\u2019acheter.',
        ],
      },
      {
        heading: 'Notre approche éditoriale',
        paragraphs: [
          'Le contenu est créé en privilégiant la clarté, l\u2019exactitude et l\u2019honnêteté. Nous révisons et mettons à jour les pages lors de changements significatifs, et nous relions les guides connexes pour vous aider à trouver ce qu\u2019il vous faut.',
          'Lisez notre politique éditoriale pour en savoir plus sur la façon dont nous créons et maintenons le contenu.',
        ],
      },
    ],
  },
  pl: {
    title: 'O USFANSLINK',
    metaDescription: 'Poznaj USFANSLINK — niezależny zasób informacyjny o arkuszu USFANS, stworzony, by pomóc Ci badać produkty, sprzedawców i zasoby zakupowe.',
    intro: [
      'USFANSLINK to niezależny zasób informacyjny. Pomagamy badać arkusz USFANS, kategorie produktów, sprzedawców, wysyłkę i przewodniki zakupowe bez przedzierania się przez rozproszone lub mylące informacje.',
      'Nie jesteśmy powiązani, wspierani ani sponsorowani przez żadnego sprzedawcę, platformę czy markę wymienioną na tej stronie. Nasz cel jest prosty: dać Ci jasne, oryginalne i uczciwe wskazówki, abyś sam podejmował świadome decyzje.',
    ],
    sections: [
      {
        heading: 'Co robimy',
        paragraphs: [
          'Publikujemy oryginalne przewodniki, recenzje i zasoby zakupowe skupione na arkuszu USFANS. Każda strona ma być przede wszystkim przydatna, a nie nakłaniać Cię do konkretnego zakupu.',
          'Porządkujemy kategorie produktów, wyjaśniamy, jak sprawdzać sprzedawców i wysyłkę, i kierujemy Cię na stronę główną, abyś mógł kontynuować badanie w jednym miejscu.',
        ],
      },
      {
        heading: 'Czego nie robimy',
        paragraphs: [
          'Nie gwarantujemy sprzedawców, produktów, cen, wyników wysyłki, zwrotów ani terminów dostawy. Nie publikujemy zmyślonych kuponów, rabatów, ocen ani statystyk.',
          'Gdy aktualnych informacji nie można zweryfikować, mówimy to jasno i zachęcamy do sprawdzenia aktualnych, oficjalnych informacji przed zakupem.',
        ],
      },
      {
        heading: 'Nasze podejście redakcyjne',
        paragraphs: [
          'Treści tworzymy z naciskiem na jasność, dokładność i uczciwość. Przeglądamy i aktualizujemy strony przy istotnych zmianach oraz linkujemy powiązane przewodniki, aby pomóc Ci znaleźć to, czego potrzebujesz.',
          'Przeczytaj naszą politykę redakcyjną, aby dowiedzieć się więcej o tym, jak tworzymy i utrzymujemy treści.',
        ],
      },
    ],
  },
};

export const editorialPage: Record<string, TrustPage> = {
  en: {
    title: 'Editorial Policy',
    metaDescription: 'How USFANSLINK creates, reviews and updates content — our editorial standards for accuracy, transparency and independence.',
    intro: [
      'This editorial policy explains how we create and maintain the content on USFANSLINK. Our goal is to be transparent about our process so you can judge the information you find here with confidence.',
    ],
    sections: [
      {
        heading: 'Independence',
        paragraphs: [
          'We are an independent informational resource. We are not affiliated with or paid by any seller, platform or brand we mention, and we do not accept compensation in exchange for favorable coverage.',
        ],
      },
      {
        heading: 'Accuracy and Honesty',
        paragraphs: [
          'We aim to be accurate and clear, and we clearly state when current information cannot be verified. We do not publish invented coupons, discounts, prices, ratings or statistics.',
          'When prices, availability or policies can change, we encourage you to check the current official information before making a purchase.',
        ],
      },
      {
        heading: 'How Content Is Created',
        paragraphs: [
          'Content is written to be genuinely useful, with a focus on practical guidance rather than filler. We organize topics around clear questions you are likely to have before buying.',
        ],
      },
      {
        heading: 'Updates and Corrections',
        paragraphs: [
          'We review and update pages when meaningful changes occur, and we keep the "last updated" information visible so you can see how current a page is. If you spot an error, contact us and we will review it.',
        ],
      },
    ],
  },
  es: {
    title: 'Política editorial',
    metaDescription: 'Cómo USFANSLINK crea, revisa y actualiza el contenido — nuestros estándares editoriales de precisión, transparencia e independencia.',
    intro: [
      'Esta política editorial explica cómo creamos y mantenemos el contenido de USFANSLINK. Nuestro objetivo es ser transparentes sobre nuestro proceso para que puedas juzgar la información de este sitio con confianza.',
    ],
    sections: [
      {
        heading: 'Independencia',
        paragraphs: [
          'Somos un recurso informativo independiente. No estamos afiliados ni cobramos de ningún vendedor, plataforma o marca que mencionamos, y no aceptamos compensación a cambio de una cobertura favorable.',
        ],
      },
      {
        heading: 'Precisión y honestidad',
        paragraphs: [
          'Buscamos ser precisos y claros, y dejamos claro cuándo la información actual no se puede verificar. No publicamos cupones, descuentos, precios, valoraciones ni estadísticas inventados.',
          'Cuando los precios, la disponibilidad o las políticas pueden cambiar, te animamos a revisar la información oficial actual antes de comprar.',
        ],
      },
      {
        heading: 'Cómo se crea el contenido',
        paragraphs: [
          'El contenido se escribe para ser realmente útil, con un enfoque en la orientación práctica en lugar de relleno. Organizamos los temas en torno a preguntas claras que probablemente tengas antes de comprar.',
        ],
      },
      {
        heading: 'Actualizaciones y correcciones',
        paragraphs: [
          'Revisamos y actualizamos las páginas cuando hay cambios significativos, y mantenemos visible la información de "última actualización" para que sepas lo actual que es una página. Si ves un error, contáctanos y lo revisaremos.',
        ],
      },
    ],
  },
  fr: {
    title: 'Politique éditoriale',
    metaDescription: 'Comment USFANSLINK crée, révise et met à jour son contenu — nos standards éditoriaux de précision, de transparence et d\u2019indépendance.',
    intro: [
      'Cette politique éditoriale explique comment nous créons et maintenons le contenu d\u2019USFANSLINK. Notre objectif est d\u2019être transparents sur notre processus pour que vous puissiez juger l\u2019information de ce site en confiance.',
    ],
    sections: [
      {
        heading: 'Indépendance',
        paragraphs: [
          'Nous sommes une ressource informative indépendante. Nous ne sommes affiliés ni rémunérés par aucun vendeur, plateforme ou marque que nous mentionnons, et nous n\u2019acceptons aucune compensation en échange d\u2019une couverture favorable.',
        ],
      },
      {
        heading: 'Exactitude et honnêteté',
        paragraphs: [
          'Nous visons l\u2019exactitude et la clarté, et nous indiquons clairement quand l\u2019information actuelle ne peut pas être vérifiée. Nous ne publions pas de coupons, remises, prix, notes ou statistiques inventés.',
          'Quand les prix, la disponibilité ou les politiques peuvent changer, nous vous encourageons à consulter l\u2019information officielle actuelle avant d\u2019acheter.',
        ],
      },
      {
        heading: 'Comment le contenu est créé',
        paragraphs: [
          'Le contenu est écrit pour être réellement utile, en privilégiant des conseils pratiques plutôt que du remplissage. Nous organisons les sujets autour des questions claires que vous êtes susceptible de vous poser avant d\u2019acheter.',
        ],
      },
      {
        heading: 'Mises à jour et corrections',
        paragraphs: [
          'Nous révisons et mettons à jour les pages lors de changements significatifs, et nous gardons visible l\u2019information « dernière mise à jour » pour que vous sachiez à quel point une page est actuelle. Si vous repérez une erreur, contactez-nous et nous la réviserons.',
        ],
      },
    ],
  },
  pl: {
    title: 'Polityka redakcyjna',
    metaDescription: 'Jak USFANSLINK tworzy, przegląda i aktualizuje treści — nasze standardy redakcyjne dokładności, przejrzystości i niezależności.',
    intro: [
      'Ta polityka redakcyjna wyjaśnia, jak tworzymy i utrzymujemy treści w USFANSLINK. Naszym celem jest przejrzystość procesu, abyś mógł z pewnością oceniać informacje na tej stronie.',
    ],
    sections: [
      {
        heading: 'Niezależność',
        paragraphs: [
          'Jesteśmy niezależnym zasobem informacyjnym. Nie jesteśmy powiązani ani opłacani przez żadnego sprzedawcę, platformę czy markę, o których wspominamy, i nie przyjmujemy wynagrodzenia w zamian za przychylne opisy.',
        ],
      },
      {
        heading: 'Dokładność i uczciwość',
        paragraphs: [
          'Dążymy do dokładności i jasności oraz wyraźnie zaznaczamy, gdy aktualnych informacji nie można zweryfikować. Nie publikujemy zmyślonych kuponów, rabatów, cen, ocen ani statystyk.',
          'Gdy ceny, dostępność lub zasady mogą się zmieniać, zachęcamy do sprawdzenia aktualnych, oficjalnych informacji przed zakupem.',
        ],
      },
      {
        heading: 'Jak powstają treści',
        paragraphs: [
          'Treści piszemy tak, by były naprawdę przydatne, z naciskiem na praktyczne wskazówki, a nie wypełniacze. Tematy porządkujemy wokół jasnych pytań, które możesz mieć przed zakupem.',
        ],
      },
      {
        heading: 'Aktualizacje i poprawki',
        paragraphs: [
          'Przeglądamy i aktualizujemy strony przy istotnych zmianach oraz utrzymujemy widoczną informację „ostatnia aktualizacja", abyś wiedział, jak aktualna jest strona. Jeśli zauważysz błąd, skontaktuj się z nami, a my go przejrzymy.',
        ],
      },
    ],
  },
};

export const privacyPage: Record<string, TrustPage> = {
  en: {
    title: 'Privacy Policy',
    metaDescription: 'The USFANSLINK privacy policy — what information we collect, how we use it, and your choices regarding cookies and third-party links.',
    intro: [
      'This privacy policy explains what information USFANSLINK collects, how we use it, and the choices you have. We keep things simple and only collect what is genuinely needed.',
    ],
    sections: [
      {
        heading: 'Information You Provide',
        paragraphs: [
          'If you contact us through our contact form, we collect the information you submit — such as your name, email address and message — so we can respond to your inquiry.',
        ],
      },
      {
        heading: 'Information Collected Automatically',
        paragraphs: [
          'Like most websites, we may collect basic technical information automatically, such as your browser type, device information and pages visited, through cookies or similar technologies. This helps us understand how the site is used.',
        ],
      },
      {
        heading: 'How We Use Information',
        paragraphs: [
          'We use the information we collect to respond to inquiries and to understand and improve the site. We do not sell your personal information to third parties.',
        ],
      },
      {
        heading: 'Third-Party Links',
        paragraphs: [
          'This site contains links to third-party websites, including the main website. We are not responsible for the privacy practices of those sites, and we encourage you to review their policies before providing information.',
        ],
      },
      {
        heading: 'Your Choices',
        paragraphs: [
          'You can control cookies through your browser settings. If you have questions about this policy or your information, contact us through our contact page.',
        ],
      },
      {
        heading: 'Changes to This Policy',
        paragraphs: [
          'We may update this privacy policy from time to time. Any changes will be posted on this page with a revised date.',
        ],
      },
    ],
  },
  es: {
    title: 'Política de privacidad',
    metaDescription: 'La política de privacidad de USFANSLINK — qué información recopilamos, cómo la usamos y tus opciones sobre cookies y enlaces de terceros.',
    intro: [
      'Esta política de privacidad explica qué información recopila USFANSLINK, cómo la usamos y las opciones que tienes. Mantenemos las cosas simples y solo recopilamos lo realmente necesario.',
    ],
    sections: [
      {
        heading: 'Información que proporcionas',
        paragraphs: [
          'Si nos contactas a través del formulario, recopilamos la información que envías — como tu nombre, correo y mensaje — para poder responder a tu consulta.',
        ],
      },
      {
        heading: 'Información recopilada automáticamente',
        paragraphs: [
          'Como la mayoría de los sitios, podemos recopilar automáticamente información técnica básica, como el tipo de navegador, el dispositivo y las páginas visitadas, mediante cookies o tecnologías similares. Esto nos ayuda a entender cómo se usa el sitio.',
        ],
      },
      {
        heading: 'Cómo usamos la información',
        paragraphs: [
          'Usamos la información para responder consultas y para entender y mejorar el sitio. No vendemos tu información personal a terceros.',
        ],
      },
      {
        heading: 'Enlaces de terceros',
        paragraphs: [
          'Este sitio contiene enlaces a sitios de terceros, incluido el sitio principal. No somos responsables de las prácticas de privacidad de esos sitios, y te animamos a revisar sus políticas antes de proporcionar información.',
        ],
      },
      {
        heading: 'Tus opciones',
        paragraphs: [
          'Puedes controlar las cookies desde la configuración de tu navegador. Si tienes preguntas sobre esta política o tu información, contáctanos a través de nuestra página de contacto.',
        ],
      },
      {
        heading: 'Cambios en esta política',
        paragraphs: [
          'Podemos actualizar esta política de privacidad ocasionalmente. Cualquier cambio se publicará en esta página con una fecha revisada.',
        ],
      },
    ],
  },
  fr: {
    title: 'Politique de confidentialité',
    metaDescription: 'La politique de confidentialité d\u2019USFANSLINK — quelles informations nous collectons, comment nous les utilisons et vos choix concernant les cookies et les liens tiers.',
    intro: [
      'Cette politique de confidentialité explique quelles informations USFANSLINK collecte, comment nous les utilisons et les choix dont vous disposez. Nous restons simples et ne collectons que le strict nécessaire.',
    ],
    sections: [
      {
        heading: 'Informations que vous fournissez',
        paragraphs: [
          'Si vous nous contactez via le formulaire, nous collectons les informations que vous envoyez — comme votre nom, votre email et votre message — afin de répondre à votre demande.',
        ],
      },
      {
        heading: 'Informations collectées automatiquement',
        paragraphs: [
          'Comme la plupart des sites, nous pouvons collecter automatiquement des informations techniques de base, comme le type de navigateur, l\u2019appareil et les pages visitées, via des cookies ou technologies similaires. Cela nous aide à comprendre l\u2019usage du site.',
        ],
      },
      {
        heading: 'Comment nous utilisons les informations',
        paragraphs: [
          'Nous utilisons les informations pour répondre aux demandes et pour comprendre et améliorer le site. Nous ne vendons pas vos informations personnelles à des tiers.',
        ],
      },
      {
        heading: 'Liens tiers',
        paragraphs: [
          'Ce site contient des liens vers des sites tiers, y compris le site principal. Nous ne sommes pas responsables des pratiques de confidentialité de ces sites, et nous vous encourageons à consulter leurs politiques avant de fournir des informations.',
        ],
      },
      {
        heading: 'Vos choix',
        paragraphs: [
          'Vous pouvez contrôler les cookies via les paramètres de votre navigateur. Si vous avez des questions sur cette politique ou vos informations, contactez-nous via notre page de contact.',
        ],
      },
      {
        heading: 'Modifications de cette politique',
        paragraphs: [
          'Nous pouvons mettre à jour cette politique de confidentialité de temps à autre. Tout changement sera publié sur cette page avec une date révisée.',
        ],
      },
    ],
  },
  pl: {
    title: 'Polityka prywatności',
    metaDescription: 'Polityka prywatności USFANSLINK — jakie informacje zbieramy, jak je wykorzystujemy oraz Twoje wybory dotyczące plików cookie i linków zewnętrznych.',
    intro: [
      'Ta polityka prywatności wyjaśnia, jakie informacje zbiera USFANSLINK, jak je wykorzystujemy i jakie masz wybory. Trzymamy się prostoty i zbieramy tylko to, co naprawdę potrzebne.',
    ],
    sections: [
      {
        heading: 'Informacje, które podajesz',
        paragraphs: [
          'Jeśli kontaktujesz się z nami przez formularz, zbieramy informacje, które przesyłasz — takie jak imię, adres e-mail i wiadomość — aby móc odpowiedzieć na zapytanie.',
        ],
      },
      {
        heading: 'Informacje zbierane automatycznie',
        paragraphs: [
          'Jak większość stron, możemy automatycznie zbierać podstawowe informacje techniczne, takie jak typ przeglądarki, urządzenie i odwiedzane strony, za pomocą plików cookie lub podobnych technologii. Pomaga nam to zrozumieć, jak strona jest używana.',
        ],
      },
      {
        heading: 'Jak wykorzystujemy informacje',
        paragraphs: [
          'Wykorzystujemy informacje do odpowiadania na zapytania oraz do zrozumienia i ulepszania strony. Nie sprzedajemy Twoich danych osobowych podmiotom trzecim.',
        ],
      },
      {
        heading: 'Linki zewnętrzne',
        paragraphs: [
          'Ta strona zawiera linki do witryn zewnętrznych, w tym do strony głównej. Nie odpowiadamy za praktyki prywatności tych witryn i zachęcamy do zapoznania się z ich politykami przed podaniem informacji.',
        ],
      },
      {
        heading: 'Twoje wybory',
        paragraphs: [
          'Możesz kontrolować pliki cookie w ustawieniach przeglądarki. Jeśli masz pytania o tę politykę lub swoje dane, skontaktuj się z nami przez stronę kontaktową.',
        ],
      },
      {
        heading: 'Zmiany tej polityki',
        paragraphs: [
          'Możemy od czasu do czasu aktualizować tę politykę prywatności. Wszelkie zmiany będą publikowane na tej stronie z nową datą.',
        ],
      },
    ],
  },
};